import { useMemo } from 'react'
import { Link, Navigate, useParams } from 'react-router-dom'
import { AlertTriangle, ArrowRight, Check, RotateCcw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { WavePlanningGrid } from '@/components/WavePlanning/WavePlanningGrid'
import { WavePlanningChart } from '@/components/WavePlanning/WavePlanningChart'
import { WavePlanningSummary } from '@/components/WavePlanning/WavePlanningSummary'
import {
  WAVE_CALENDARS,
  WAVE_FIXTURES,
  REFERENCE_WEEKS,
} from '@/components/WavePlanning/data'
import {
  calculateWavePlanning,
  formatPercent,
  shiftWaveDate,
} from '@/components/WavePlanning/model'
import { useWavePlanningStore } from '@/stores/useWavePlanningStore'
import { WavePlanningNav } from '@/components/WavePlanning/WavePlanningNav'
import {
  getWavePlan,
  wavePlanPath,
  wavePlanMfpLabel,
  type WavePlan,
} from '@/components/WavePlanning/plans'
import { WavePlanStatus } from '@/components/WavePlanning/WavePlanStatus'
import { WavePlansBackLink } from '@/components/WavePlanning/WavePlansBackLink'

export default function WavePlanning() {
  const { planId } = useParams()
  const plan = getWavePlan(planId)
  if (!plan) return <Navigate to="/wave-planning" replace />
  return <OverallPlanning key={plan.id} plan={plan} />
}

function OverallPlanning({ plan }: { plan: WavePlan }) {
  const { calendarId, subclassId } = plan
  const { drafts, updateWave, reset } = useWavePlanningStore()
  const calendar = WAVE_CALENDARS.find(item => item.id === calendarId)!
  const fixture = WAVE_FIXTURES.find(item => item.id === subclassId)!
  const key = `${calendarId}:${subclassId}`
  const percentages = drafts[key] ?? fixture.plannedWaves
  const weeks = useMemo(
    () =>
      REFERENCE_WEEKS.map(week => ({
        ...week,
        date: shiftWaveDate(week.date, calendar.offsetDays),
      })),
    [calendar.offsetDays]
  )
  const result = useMemo(
    () => calculateWavePlanning(fixture, weeks, percentages),
    [fixture, weeks, percentages]
  )
  const changed = percentages.some(
    (value, index) => value !== fixture.plannedWaves[index]
  )
  const balanced = Math.abs(result.total - 1) < 0.0000001
  const belowMinimum = result.firstWave + 0.0000001 < result.minimumFirstWave

  return (
    <div className="flex min-w-0 flex-col gap-5 p-4 md:p-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <WavePlansBackLink />
          <h1 className="text-2xl font-semibold text-foreground">
            Wave Planning
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Plan weekly receipts and compare them with seasonal demand.
          </p>
        </div>
        <p className="pt-1 text-xs text-muted-foreground">
          Demo data · Changes reset on reload
        </p>
      </header>
      <WavePlanningNav planId={plan.id} />

      <Card className="gap-4 p-4 shadow-none">
        <div className="flex flex-wrap items-center gap-3">
          <h2 className="text-lg font-semibold">{plan.name}</h2>
          <WavePlanStatus status={plan.status} />
        </div>
        <dl className="grid gap-4 text-sm sm:grid-cols-3">
          <div>
            <dt className="text-xs text-muted-foreground">Subclass</dt>
            <dd className="mt-1 font-medium">
              {subclassId} · {fixture.name}
            </dd>
          </div>
          <div>
            <dt className="text-xs text-muted-foreground">Calendar</dt>
            <dd className="mt-1 font-medium">{calendar.label}</dd>
          </div>
          <div>
            <dt className="text-xs text-muted-foreground">MFP Plan</dt>
            <dd className="mt-1 font-medium">{wavePlanMfpLabel(plan)}</dd>
          </div>
        </dl>
        <div className="flex flex-wrap items-center justify-between gap-3 border-t pt-3">
          <div>
            <p className="text-xs font-medium">{fixture.code}</p>
            <p className="mt-1 text-[11px] text-muted-foreground">
              {fixture.description}
            </p>
          </div>
          <Button asChild size="sm">
            <Link to={wavePlanPath(plan.id, 'skus')}>
              View SKU detail <ArrowRight size={14} />
            </Link>
          </Button>
        </div>
      </Card>
      {calendar.offsetDays > 0 && (
        <p className="-mt-2 text-xs text-muted-foreground">
          2027 demo uses the 2026 demand profile and purchase quantities, with
          dates shifted by 52 weeks.
        </p>
      )}

      <div className="grid min-w-0 gap-5 xl:grid-cols-[minmax(340px,0.9fr)_minmax(0,1.6fr)]">
        <WavePlanningSummary
          fixture={fixture}
          result={result}
          year={calendar.year}
          setDate={shiftWaveDate(fixture.setDate, calendar.offsetDays)}
        />
        <WavePlanningChart
          key={key}
          weeks={result.weeks}
          setWeek={fixture.setWeek}
        />
      </div>

      <Card className="min-w-0 overflow-hidden shadow-none">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b px-4 py-3">
          <div>
            <h2 className="text-sm font-semibold">Weekly wave plan</h2>
            <p className="mt-1 text-xs text-muted-foreground">
              Edit the highlighted percentages. All results update
              automatically.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-4">
            <div
              role="status"
              className={`flex items-center gap-1.5 text-xs tabular-nums ${balanced ? 'text-foreground' : 'text-destructive'}`}
            >
              {balanced ? <Check size={14} /> : <AlertTriangle size={14} />}
              <span>
                <strong data-testid="wave-total">
                  {formatPercent(result.total)}
                </strong>{' '}
                allocated
                {!balanced &&
                  ` · ${formatPercent(Math.abs(1 - result.total))} ${result.total < 1 ? 'remaining' : 'over target'}`}
              </span>
            </div>
            <Button
              variant="outline"
              size="sm"
              disabled={!changed}
              onClick={() => reset(calendarId, subclassId)}
            >
              <RotateCcw size={14} />
              Reset changes
            </Button>
          </div>
        </div>
        {belowMinimum && (
          <p
            role="status"
            className="flex items-center gap-2 border-b bg-accent px-4 py-2.5 text-xs text-destructive"
          >
            <AlertTriangle size={14} className="shrink-0" />
            First wave is {formatPercent(result.firstWave)}, below the{' '}
            {formatPercent(result.minimumFirstWave)} minimum.
          </p>
        )}
        <WavePlanningGrid
          key={key}
          weeks={result.weeks}
          timeline={weeks}
          year={calendar.year}
          setWeek={fixture.setWeek}
          onChange={(index, percent) =>
            updateWave(calendarId, subclassId, index, percent)
          }
        />
      </Card>
    </div>
  )
}
